// A connection's setup status as governance-service serves it (Agion#2673,
// ADR-20261002, challenge record 2026-10-03-connection-setup-status-server-
// verdict): Ready, Needs setup naming the first unfinished step, or Not
// working with the reason, and every step's state. governance-service works
// it out; both interfaces read it here and word its reason code through
// setupStatusWords. A read that carries no status, or one this client does
// not recognise (a server from another release), shows no verdict at all.
import type { TFunction } from 'i18next'

/** The query parameter that asks a connections read for the facts and the status. */
export const INCLUDE_SETUP = { include_setup: 'true' } as const

export const SETUP_STATES = ['ready', 'needs_setup', 'not_working'] as const
export type SetupState = (typeof SETUP_STATES)[number]

export const SERVED_STEPS = ['sign_in', 'hosts', 'data_rules'] as const
export type ServedStep = (typeof SERVED_STEPS)[number]

const STEP_STATES = ['done', 'todo', 'failed'] as const
export type ServedStepState = (typeof STEP_STATES)[number]

export const SETUP_REASONS = [
  'ready',
  'sign_in_failed',
  'sign_in_error_state',
  'sign_in_verification_required',
  'no_hosts',
  'classification_missing',
  // The data location is not confirmed yet (challenge record
  // 2026-10-04-governance-a3-source-policy-completion (4)): served after
  // classification_missing on the data_rules step once six-level governance
  // is active for the organisation. Known here before the server emits it.
  'location_missing',
  'questions_unanswered',
] as const
export type SetupReason = (typeof SETUP_REASONS)[number]

export interface ConnectionSetupStatus {
  status: SetupState
  /** The first step that failed or is still to do; absent when Ready. */
  step?: ServedStep
  reason: SetupReason
  /** The health check's own reason (message) or the unanswered questions (count). */
  params?: { message?: string; count?: number }
  steps: { step: ServedStep; state: ServedStepState }[]
}

const oneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
  typeof value === 'string' && (values as readonly string[]).includes(value)

function servedParams(raw: unknown): ConnectionSetupStatus['params'] | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const { message, count } = raw as { message?: unknown; count?: unknown }
  const params: { message?: string; count?: number } = {}
  if (typeof message === 'string' && message.trim()) params.message = message.trim()
  if (typeof count === 'number' && Number.isSafeInteger(count) && count >= 0) params.count = count
  return Object.keys(params).length > 0 ? params : undefined
}

/**
 * setup_status of one served record, in either key case; undefined when the
 * record carries none or one this client cannot read in full.
 */
export function servedSetupStatus(record: unknown): ConnectionSetupStatus | undefined {
  if (!record || typeof record !== 'object') return undefined
  const source = record as Record<string, unknown>
  const raw = source.setupStatus ?? source.setup_status
  if (!raw || typeof raw !== 'object') return undefined
  const { status, step, reason, params, steps } = raw as Record<string, unknown>
  if (!oneOf(SETUP_STATES, status) || !oneOf(SETUP_REASONS, reason) || !Array.isArray(steps)) return undefined
  if (step !== undefined && step !== null && !oneOf(SERVED_STEPS, step)) return undefined
  const served: ConnectionSetupStatus['steps'] = []
  for (const entry of steps) {
    const { step: id, state } = (entry ?? {}) as { step?: unknown; state?: unknown }
    if (!oneOf(SERVED_STEPS, id) || !oneOf(STEP_STATES, state)) return undefined
    served.push({ step: id, state })
  }
  if (served.length !== SERVED_STEPS.length || served.some((entry, index) => entry.step !== SERVED_STEPS[index])) return undefined
  const words = servedParams(params)
  // The count is what the words name; without it the reason cannot be stated.
  if (reason === 'questions_unanswered' && words?.count === undefined) return undefined
  return { status, reason, steps: served, ...(oneOf(SERVED_STEPS, step) ? { step } : {}), ...(words ? { params: words } : {}) }
}

// ---------------------------------------------------------------------------
// Words: the one mapper from the served codes to text, for both interfaces
// ---------------------------------------------------------------------------

const K = 'pages:connectionSetup'

export type SetupTone = 'success' | 'warning' | 'error'

export const SETUP_TONE: Record<SetupState, SetupTone> = {
  ready: 'success',
  needs_setup: 'warning',
  not_working: 'error',
}

/** The status in one or two words: Ready, Needs setup, Not working. */
export function setupStatusLabel(t: TFunction, status: ConnectionSetupStatus): string {
  return t(`${K}.status.${status.status}`)
}

/**
 * Why the connection is not Ready, in words: the health check's own reason
 * when the server passed it on, otherwise the reason code's sentence.
 * Undefined for Ready, and for the reason code ready, which has no sentence.
 */
export function setupStatusReason(t: TFunction, status: ConnectionSetupStatus): string | undefined {
  if (status.status === 'ready' || status.reason === 'ready') return undefined
  if ((status.reason === 'sign_in_failed' || status.reason === 'sign_in_error_state') && status.params?.message) {
    return status.params.message
  }
  if (status.reason === 'questions_unanswered') {
    return t(`${K}.reason.questions_unanswered`, { count: status.params?.count })
  }
  return t(`${K}.reason.${status.reason}`)
}
